import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import FeatureSection from "../components/FeatureSection";
import Link from "next/link";
import AntipiracySnipe from "../components/AntipiracySnipe";

export default function FilmFestivals() {
  return (
    <Layout title="Security">
      <div>
        <PageHeader
          label="Security on Eventive"
          headline="Industry-leading protection."
          subhead={
            <>
              <div>
                We deploy the most advanced security solutions available, both
                to protect films and media as well as to secure customer data.
                Trusted by leading distributors and studios including A24, Sony
                Pictures Classics, Starz, Showtime, National Geographic, and
                many more.
              </div>
              <div className="mt-8 sm:mt-16 flex justify-center">
                <AntipiracySnipe />
              </div>
            </>
          }
        />
        <div className="lg:pt-6">
          <div className="relative max-w-xl mx-auto px-6 sm:px-6 lg:px-8 lg:max-w-screen-xl">
            <FeatureSection
              imageSide="right"
              title="The most secure video on-demand and livestreaming platform."
              description="MPAA-certified, studio-trusted, battle-tested security that your content deserves."
              bullets={[
                {
                  key: "org",
                  title:
                    "Multi-platform digital rights management (DRM) technology",
                  description:
                    "Secure films with complete multi-platform DRM (Widevine, PlayReady, and FairPlay) to prevent unauthorized screen recording and downloads.",
                },
                {
                  key: "audience",
                  title: "Livestreaming with real-time DRM",
                  description:
                    "Encrypt your livestreams with real-time DRM using the same studio-grade DRM available for on-demand content. Trust Eventive for real-time premieres and more.",
                },
                {
                  key: "test",
                  title: "Forensic and visible watermarking",
                  description:
                    "For pre-release and screener content, enable full per-session forensic watermarking. Deliver a unique video file to each viewer with invisible tracking marks to uniquely identify leaks. Embed user names, emails, IP addresses, and more directly on the video.",
                },
              ]}
              image={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="bg-red-500 p-12 sm:p-20 rounded-full text-white w-1/2 h-1/2">
                    <svg
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      className="w-full h-full"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                  </div>
                </div>
              }
            />
            <FeatureSection
              imageSide="left"
              title="Enhanced security features that you control."
              description="State-of-the-art, advanced features to stay at the forefront of content security."
              bullets={[
                {
                  key: "org",
                  title: "Georestrictions set per-screening",
                  description:
                    "Choose territories to allow or exclude for each individual screening.  Country, state and province level, or add custom territories as small as a single postal code.",
                },
                {
                  key: "audience",
                  title: "Advanced VPN and proxy blocking",
                  description:
                    "Validate access and block spoofing to ensure only authenticated users can view your content.",
                },
                {
                  key: "test",
                  title: "Concurrent stream restrictions",
                  description:
                    "Automatically detect and disable concurrent streams and other forbidden behaviors.",
                },
              ]}
              image={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="bg-red-500 p-12 sm:p-20 rounded-full text-white w-1/2 h-1/2">
                    <svg
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="w-full h-full"
                    >
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>{" "}
                  </div>
                </div>
              }
            />
            <FeatureSection
              imageSide="right"
              title="Customer privacy and security."
              description="We uphold the most stringent standards of consumer privacy and payment security. We never share customer data with third parties, and all our partners commit to upholding the Eventive privacy policy."
              bullets={[
                {
                  key: "audience",
                  title: "PCI compliance for the utmost payment security",
                  description:
                    "All customer card data is submitted securely to our payment processor, Stripe, using the most stringent, PCI Level 1 compliant security protocols.",
                },
                {
                  key: "org",
                  title: "GDPR and Privacy Shield certified",
                  description: (
                    <span>
                      Our privacy policy is fully GDPR compliant, and we are a
                      member of the{" "}
                      <a
                        href="https://www.privacyshield.gov/welcome"
                        target="_blank"
                        className="underline hover:opacity-75" rel="noreferrer"
                      >
                        US/EU Privacy Shield Program
                      </a>
                      , so you and your audience can be sure that your
                      information is safe.
                    </span>
                  ),
                },
                // {
                //   key: "mailing",
                //   title: "Mailing list opt-ins and more",
                //   description:
                //     "Ensure your customers have control of their personal information. Allow customers to opt-in to mailings, verify agreement, and more.",
                // },
              ]}
              image={
                <div className="w-full h-full flex items-center justify-center">
                  <div className="bg-red-500 p-12 sm:p-20 rounded-full text-white w-1/2 h-1/2">
                    <svg
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      className="w-full h-full"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>{" "}
                  </div>
                </div>
              }
            />
          </div>
        </div>
        <section className="py-6 bg-white overflow-hidden md:py-12 lg:py-18"></section>
      </div>
    </Layout>
  );
}
